import { useEffect, useState } from 'react';
import type { Tenant } from '../lib/database.types';
import { formatPrice } from '../lib/cart';
import Socials from './Socials';

/**
 * Header / navigation de la vitrine — porté À L'IDENTIQUE du legacy (Food Kourt / Big Burger / TFK).
 * Sticky, fond blanc qui prend une ombre au scroll. Logo (image tenant ou pastille lettre) + ville,
 * liens Accueil / Menu / Infos & accès (soulignement animé), icônes réseaux, panier persistant,
 * bouton « Commander » de marque, et menu burger sur mobile. Tout piloté par le tenant.
 */
interface StoreHeaderProps {
  tenant: Tenant;
  cartCount: number;
  cartSubtotal: number;
  currency: 'EUR' | 'CHF';
  onHome: () => void;
  onMenu: () => void;
  onInfos: () => void;
  onCart: () => void;
  /** Lien « Ma cagnotte fidélité » (parité C2) — affiché seulement si fourni (tenant fidélité actif). */
  onLoyalty?: () => void;
}

export default function StoreHeader({
  tenant,
  cartCount,
  cartSubtotal,
  currency,
  onHome,
  onMenu,
  onInfos,
  onCart,
  onLoyalty,
}: StoreHeaderProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navItems: { label: string; action: () => void }[] = [
    { label: 'Accueil', action: onHome },
    { label: 'Menu', action: onMenu },
    { label: 'Infos & accès', action: onInfos },
  ];

  const logo = tenant.logo_url ? (
    <img src={tenant.logo_url} alt={tenant.name} className="h-11 w-11 rounded-full object-contain" />
  ) : (
    <span
      className="flex h-11 w-11 items-center justify-center rounded-full text-base font-black text-white"
      style={{ background: 'var(--brand-primary)' }}
    >
      {tenant.name.slice(0, 1)}
    </span>
  );

  return (
    <header
      className={
        'sticky top-0 z-40 transition-all ' +
        (scrolled ? 'bg-white/95 shadow-sm backdrop-blur' : 'bg-white')
      }
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 lg:h-20 lg:px-6">
        {/* Logo + ville */}
        <button onClick={onHome} className="flex items-center gap-3">
          {logo}
          <span className="flex flex-col items-start leading-none">
            <span className="font-bold" style={{ color: 'var(--brand-primary)' }}>
              {tenant.name}
            </span>
            {tenant.city && (
              <span className="mt-0.5 text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">
                {tenant.city}
              </span>
            )}
          </span>
        </button>

        {/* Nav desktop */}
        <nav className="hidden items-center gap-8 lg:flex">
          {navItems.map((it) => (
            <button
              key={it.label}
              onClick={it.action}
              className="underline-grow text-sm font-semibold text-gray-700"
            >
              {it.label}
            </button>
          ))}
          {onLoyalty && (
            <button
              onClick={onLoyalty}
              className="underline-grow flex items-center gap-1 text-sm font-semibold text-gray-700"
            >
              <span aria-hidden>🎁</span> Ma cagnotte fidélité
            </button>
          )}
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-gray-500">Suivez-nous</span>
            <Socials socials={tenant.socials} />
          </div>
        </nav>

        {/* Actions droite */}
        <div className="flex items-center gap-2 lg:gap-3">
          {cartCount > 0 && (
            <button
              onClick={onCart}
              className="relative flex items-center gap-2 rounded-xl bg-gray-100 px-3 py-2 text-sm font-bold transition hover:bg-gray-200 lg:px-4 lg:py-2.5"
            >
              <span className="text-lg">🛒</span>
              <span className="hidden sm:inline">
                {cartCount} article{cartCount > 1 ? 's' : ''}
              </span>
              <span className="sm:hidden">{cartCount}</span>
              <span className="hidden font-extrabold md:inline" style={{ color: 'var(--brand-primary)' }}>
                {formatPrice(cartSubtotal, currency)}
              </span>
              <span
                className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full text-xs font-bold text-white"
                style={{ background: 'var(--brand-primary)' }}
              >
                {cartCount}
              </span>
            </button>
          )}

          <button
            onClick={onMenu}
            className="hidden items-center gap-2 rounded-xl px-5 py-2.5 font-bold text-white shadow transition active:scale-95 lg:flex"
            style={{ background: 'var(--brand-primary)' }}
          >
            <span>{cartCount > 0 ? 'Continuer' : 'Commander'}</span>
            <span aria-hidden="true">→</span>
          </button>

          <button
            onClick={() => setMenuOpen((o) => !o)}
            aria-label="Menu"
            className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-xl lg:hidden"
          >
            {menuOpen ? '✕' : '☰'}
          </button>
        </div>
      </div>

      {/* Menu mobile */}
      {menuOpen && (
        <div className="fade-in border-t border-gray-100 bg-white lg:hidden">
          <nav className="space-y-2 px-4 py-4">
            {navItems.map((it) => (
              <button
                key={it.label}
                onClick={() => {
                  it.action();
                  setMenuOpen(false);
                }}
                className="w-full rounded-xl p-3 text-left font-semibold text-gray-700 hover:bg-gray-50"
              >
                {it.label}
              </button>
            ))}
            {onLoyalty && (
              <button
                onClick={() => {
                  onLoyalty();
                  setMenuOpen(false);
                }}
                className="w-full rounded-xl p-3 text-left font-semibold text-gray-700 hover:bg-gray-50"
              >
                🎁 Ma cagnotte fidélité
              </button>
            )}
            <div className="flex items-center gap-2 px-3 py-2">
              <span className="text-sm font-semibold text-gray-500">Suivez-nous</span>
              <Socials socials={tenant.socials} />
            </div>
            <button
              onClick={() => {
                onMenu();
                setMenuOpen(false);
              }}
              className="mt-2 w-full rounded-xl py-3 font-bold text-white"
              style={{ background: 'var(--brand-primary)' }}
            >
              Commander en ligne →
            </button>
          </nav>
        </div>
      )}
    </header>
  );
}
